/**
 * Pure parsers for the query params an embed URL carries.
 *
 * These are deliberately separate from `pinEmbedPalette` (which touches the store and the
 * DOM) so they can be unit tested directly, and shared rather than reimplemented: the theme
 * check previously existed twice with two different sets of accepted param names, which is
 * the same kind of drift that produced the invisible-ticker-text bug.
 */

/** Every spelling of the theme param that a live embed might carry. */
const THEME_PARAMS = ['colorTheme', 'color_scheme', 'theme'] as const;

/** Anything with a `get`, so this accepts both `URLSearchParams` and Next's readonly wrapper. */
export interface ReadableParams {
    get(name: string): string | null;
}

/**
 * The palette an embed asked for, defaulting to light.
 *
 * All three spellings are accepted because different embed generations use different names
 * and partner embeds are live on more than one: the event/market embeds send `color_scheme`,
 * the gallery's generated code sends `colorTheme`, and the ticker on news.kalshi.com sends
 * `theme`.
 */
export function embedPaletteFromParams(params: ReadableParams): 'light' | 'dark' {
    // Any spelling asking for dark wins, matching how the event route resolves it
    // (`color_scheme === 'dark' || colorTheme === 'dark'`). First-present-wins precedence
    // disagreed with that route when an older embed carried two generations of the param at
    // once, and the two then pinned different palettes on top of each other.
    return THEME_PARAMS.some((name) => params.get(name) === 'dark') ? 'dark' : 'light';
}

/** Lower bound keeps a near-zero multiplier from stalling the strip; upper bound keeps it readable. */
const MIN_SPEED = 0.1;
const MAX_SPEED = 5;

/**
 * Ticker scroll-speed multiplier: >1 scrolls faster, <1 slower, 1 keeps the default pace.
 * Anything missing, unparseable or non-positive falls back to 1 rather than producing a
 * zero or negative animation duration, which would freeze the strip outright.
 */
export function resolveTickerSpeed(raw: string | number | null | undefined): number {
    const value = typeof raw === 'number' ? raw : Number.parseFloat(raw ?? '');
    if (!Number.isFinite(value) || value <= 0) return 1;
    return Math.min(Math.max(value, MIN_SPEED), MAX_SPEED);
}

/**
 * A boolean embed param: `true`/`1` or `false`/`0` (any case), else `fallback`. For new params;
 * the older widgets' flags keep their own historical spellings for live embeds.
 */
export function booleanParam(params: ReadableParams, name: string, fallback: boolean): boolean {
    const value = params.get(name)?.trim().toLowerCase();
    if (value === 'true' || value === '1') return true;
    if (value === 'false' || value === '0') return false;
    return fallback;
}
